import { describeCondition, formatMetricValue, type ClientStatusDto } from '@citilight-monitor/contracts';
import { formatDuration, formatRelative } from '../time/time';
import type { ChipTone } from './status';

/** Something about a client that an operator should look at, in plain language. */
export interface Issue {
  key: string;
  clientId: string;
  clientName: string;
  tone: ChipTone;
  /** "Firing", "Breaching 2 of 5", "Unreachable" … */
  state: string;
  /** "CPU usage above 85%" */
  summary: string;
  /** Current value, e.g. "92.4%", when the issue is about a metric. */
  value?: string;
  /** "for ~30 min", "last poll 50 min ago" */
  since?: string;
}

const TONE_RANK: Record<ChipTone, number> = { critical: 0, serious: 1, warning: 2, neutral: 3, good: 4 };

/** Every open issue on a client: reachability first, then firing rules, then rules counting breaches. */
export function clientIssues(status: ClientStatusDto, pollIntervalMs: number, now = Date.now()): Issue[] {
  const { client, latest, health } = status;
  const base = { clientId: client.id, clientName: client.name };
  const lastPoll = latest ? `last poll ${formatRelative(latest.timestamp, now)}` : undefined;
  const issues: Issue[] = [];

  if (health === 'unreachable') {
    const down = client.metrics.filter((m) => m.unit === 'state' && latest?.metrics[m.key] === 0).map((m) => m.label);
    issues.push({
      ...base,
      key: `${client.id}:unreachable`,
      tone: 'serious',
      state: 'Unreachable',
      summary: down.length ? `${down.join(' and ')}: no response` : 'Last poll got no response',
      since: lastPoll,
    });
  } else if (health === 'stale') {
    issues.push({ ...base, key: `${client.id}:stale`, tone: 'warning', state: 'Not reporting', summary: 'No recent polls recorded', since: lastPoll });
  } else if (health === 'no_data') {
    issues.push({ ...base, key: `${client.id}:no-data`, tone: 'neutral', state: 'No data yet', summary: 'This client has never been polled' });
  }

  const states = new Map(status.ruleStates.map((s) => [s.ruleId, s]));
  for (const rule of client.rules) {
    const state = states.get(rule.id);
    if (!state || (!state.firing && state.consecutiveBreaches === 0)) continue;
    const metric = client.metrics.find((m) => m.key === rule.metric);
    const unit = metric?.unit ?? 'number';
    // Reachability rules would repeat the "Unreachable" issue above.
    if (health === 'unreachable' && unit === 'state') continue;
    issues.push({
      ...base,
      key: `${client.id}:${rule.id}`,
      tone: state.firing ? 'critical' : 'warning',
      state: state.firing ? 'Firing' : `Breaching ${state.consecutiveBreaches} of ${rule.consecutive}`,
      summary: describeCondition(metric?.label ?? rule.metric, unit, rule.operator, rule.threshold),
      value: formatMetricValue(unit, latest?.metrics[rule.metric]),
      since: `for ~${formatDuration(state.consecutiveBreaches * pollIntervalMs)}`,
    });
  }

  return issues.sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone]);
}

/** Issues across all clients, most severe first. */
export function fleetIssues(clients: ClientStatusDto[], pollIntervalMs: number): Issue[] {
  return clients
    .flatMap((c) => clientIssues(c, pollIntervalMs))
    .sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone] || a.clientName.localeCompare(b.clientName));
}
